import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import moment from 'moment';
import useCalendarData from "hooks/useCalendarData";
import Loader from "components/loaders/Loader";
import { FaRegCalendarAlt, FaCalendarWeek, FaPlus, FaTimes, FaMapMarkerAlt, FaUsers, FaCheckCircle } from "react-icons/fa";
import AddEventModal from "./components/AddEventModal";
import CStatCard from "./components/CStatsCard";
import UpcomingEventsCard from "./components/UpcomingEvents";
import { fireSwal } from "utils/swalConfig";

// --- Sub-Components ---

// Read-only modal for displaying past event history
const EventHistoryModal = ({ event, onClose }) => {
    if (!event) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div className="relative w-full max-w-lg bg-white dark:bg-navy-800 rounded-2xl shadow-xl p-6 m-4" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-white">
                    <FaTimes size={20} />
                </button>
                
                <div className="absolute top-4 left-4 flex items-center gap-2 rounded-full bg-gray-100 dark:bg-navy-700 px-3 py-1 text-sm font-semibold text-gray-500 dark:text-gray-300">
                    <FaCheckCircle />
                    <span>Event Ended</span>
                </div>

                <h2 className="text-2xl font-bold text-navy-700 dark:text-white mt-8">{event.title}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{moment(event.start).format('dddd, MMMM Do YYYY')}</p>
                
                <div className="mt-4 space-y-2 text-gray-700 dark:text-gray-300">
                    <p className="flex items-center gap-2"><FaMapMarkerAlt className="text-gray-400" /> {event.extendedProps.location}</p>
                    <p>{event.extendedProps.description}</p>
                </div>

                {event.extendedProps.teams && event.extendedProps.teams.length > 0 && (
                    <div className="mt-6">
                        <h3 className="font-semibold text-navy-700 dark:text-white flex items-center gap-2"><FaUsers /> Participating Teams ({event.extendedProps.teams.length})</h3>
                        <div className="mt-2 max-h-40 overflow-y-auto rounded-lg bg-gray-100 dark:bg-navy-700 p-3 text-gray-800 dark:text-gray-200">
                            <ul className="space-y-1 list-disc list-inside">
                                {event.extendedProps.teams.map((team) => <li key={team.id}>{team.name}</li>)}
                            </ul>
                        </div>
                    </div>
                )}
                <div className="mt-6 flex justify-end">
                    <button onClick={onClose} className="rounded-lg bg-gray-200 dark:bg-navy-700 px-5 py-2 font-semibold text-navy-700 dark:text-white hover:bg-gray-300 dark:hover:bg-navy-600 transition-colors">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// Modal for upcoming events with reservation logic
const EventDetailModal = ({ event, onClose }) => {
    const { user, myReservationEventIds, createReservation } = useCalendarData();
    const [isReserving, setIsReserving] = useState(false);
    const [reservationDetails, setReservationDetails] = useState('');
    
    const isAlreadyReserved = useMemo(() => myReservationEventIds.includes(event?.id), [event, myReservationEventIds]);

    const handleReserve = async () => {
        if (isAlreadyReserved) return;
        if (!isReserving) {
            setIsReserving(true);
            return;
        }
        
        const timelineIdToReserve = event?.extendedProps?.reservableTimelineId;
        if (!timelineIdToReserve) {
            fireSwal({ icon: 'error', title: 'Error', text: 'No reservable slot found for this event.' });
            return;
        }

        try {
            await createReservation({ timelineId: timelineIdToReserve, notes: reservationDetails });
            fireSwal({ icon: 'success', title: 'Reservation Confirmed!', timer: 2000, showConfirmButton: false });
            onClose();
        } catch (error) {
            fireSwal({ icon: 'error', title: 'Reservation Failed', text: error.response?.data?.message || 'An error occurred.' });
        }
    };

    if (!event) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div className="relative w-full max-w-lg bg-white dark:bg-navy-800 rounded-2xl shadow-xl p-6 m-4" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-white">
                    <FaTimes size={20} />
                </button>
                <h2 className="text-2xl font-bold text-navy-700 dark:text-white">{event.title}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{moment(event.start).format('dddd, MMMM Do YYYY')}</p>
                <div className="mt-4 space-y-2 text-gray-700 dark:text-gray-300">
                    <p className="flex items-center gap-2"><FaMapMarkerAlt className="text-orange-500" /> {event.extendedProps.location}</p>
                    <p>{event.extendedProps.description}</p>
                </div>
                <div className="mt-6">
                    <h3 className="font-semibold text-navy-700 dark:text-white flex items-center gap-2"><FaUsers /> Registered Teams ({event.extendedProps.teams.length})</h3>
                    <div className="mt-2 max-h-40 overflow-y-auto rounded-lg bg-gray-100 dark:bg-navy-700 p-3 text-gray-800 dark:text-gray-200">
                         <ul className="space-y-1 list-disc list-inside">
                            {event.extendedProps.teams.length > 0 ? event.extendedProps.teams.map((team) => <li key={team.id}>{team.name}</li>) : <li>No teams registered yet.</li>}
                        </ul>
                    </div>
                </div>
                {user?.role !== 'mentor' && isReserving && !isAlreadyReserved && (
                    <div className="mt-4">
                        <textarea
                            className="w-full rounded-md border border-gray-300 dark:border-navy-600 bg-white dark:bg-navy-700 p-2 text-sm text-navy-700 dark:text-white"
                            rows="2"
                            placeholder="Add extra details for your reservation..."
                            value={reservationDetails}
                            onChange={(e) => setReservationDetails(e.target.value)}
                        ></textarea>
                    </div>
                )}
                <div className="mt-6 flex justify-end">
                    {user?.role !== 'mentor' ? (
                        <button 
                            onClick={handleReserve} 
                            disabled={isAlreadyReserved}
                            className={`rounded-lg px-5 py-2 font-semibold transition-colors ${ isAlreadyReserved ? 'bg-orange-500/30 text-white/70 cursor-not-allowed' : 'bg-orange-500 text-white hover:bg-orange-600' }`}
                        >
                            {isAlreadyReserved ? 'Reserved' : (isReserving ? 'Confirm Reservation' : 'Reserve Spot')}
                        </button>
                    ) : (
                        <button onClick={onClose} className="rounded-lg bg-gray-200 dark:bg-navy-700 px-5 py-2 font-semibold text-navy-700 dark:text-white">
                            Close
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Simple calendar event renderer - Fixed for proper text display
function renderEventContent(eventInfo) {
    const isPast = eventInfo.event.extendedProps.isPast;
    const event = eventInfo.event;
    const isReserved = eventInfo.event.extendedProps.isReserved || false;
    
    return (
        <span className={`event-title ${isPast ? 'past-event' : ''} ${isReserved ? 'reserved-event' : ''}`}>
            {event.title}
        </span>
    );
}

// Sidebar component.
const CalendarSidebar = ({ user, stats, onAddEventClick }) => (
    <div className="flex flex-col gap-6">
        <div>
            <h3 className="text-xl md:text-2xl lg:text-4xl font-bold text-navy-700 dark:text-white">My Schedule</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Welcome back, {user?.firstName || 'User'}.</p>
        </div>
        {user?.role === 'mentor' && (
            <button onClick={onAddEventClick} className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-lg font-bold text-white shadow-lg">
                <FaPlus /> Create Event
            </button>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
            <CStatCard icon={<FaRegCalendarAlt size={24} />} title="This Month" value={stats.eventsThisMonth} />
            <CStatCard icon={<FaCalendarWeek size={24} />} title="This Week" value={stats.eventsThisWeek} />
        </div>
        <UpcomingEventsCard />
    </div>
);

// Main Calendar Component
const Calendar = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [modalView, setModalView] = useState('detail');
    const { isLoading, events, user, addEvent } = useCalendarData();
    const [searchParams] = useSearchParams();
    const calendarRef = useRef(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const calendarEvents = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return events.map(event => ({
            ...event,
            extendedProps: {
                ...event.extendedProps,
                isPast: new Date(event.start) < today,
            },
        }));
    }, [events]);

    const stats = useMemo(() => {
        const now = moment();
        const eventsThisMonth = new Set(events.filter(e => moment(e.start).isSame(now, 'month')).map(e => e.id)).size;
        const eventsThisWeek = new Set(events.filter(e => moment(e.start).isSame(now, 'isoWeek')).map(e => e.id)).size;
        return { eventsThisMonth, eventsThisWeek };
    }, [events]);

    const handleEventClick = (clickInfo) => {
        if (clickInfo.event.extendedProps.isPast) {
            setModalView('history');
        } else {
            setModalView('detail');
        }
        setSelectedEvent(clickInfo.event);
    };
    
    const handleAddEvent = async (eventData) => {
        try {
            await addEvent(eventData);
            setIsAddModalOpen(false);
            fireSwal({ icon: 'success', title: 'Event Created!' });
        } catch (error) {
            fireSwal({ icon: 'error', title: 'Oops...', text: 'Failed to create the event.' });
        }
    };
    
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <Loader text="Loading calendar..." fullScreen={false} />
            </div>
        );
    }
    
    // Updated event class names function
    const getEventClassNames = (arg) => {
        const isPast = arg.event.extendedProps.isPast;
        const isReserved = arg.event.extendedProps.isReserved || false;
        const isImportant = arg.event.extendedProps.isImportant || false;
        
        const classes = ['modern-event-card'];
        
        if (isPast) {
            classes.push('past-event');
        } else if (isReserved) {
            classes.push('reserved-event');
        }
        
        if (isImportant) {
            classes.push('important-event');
        }
        
        return classes;
    };

    // Custom more link handler for better user experience
    const handleMoreLinkClick = (info) => {
        // Show a custom modal or popover with event previews
        const events = info.allEvents;
        const date = moment(info.date).format('MMMM Do, YYYY');
        
        fireSwal({
            title: `Events on ${date}`,
            html: `
                <div class="text-left space-y-2">
                    ${events.map(event => `
                        <div class="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border-l-4 border-orange-500">
                            <h4 class="font-semibold text-orange-900 dark:text-orange-200">${event.title}</h4>
                            <p class="text-sm text-gray-600 dark:text-gray-400">
                                📍 ${event.extendedProps.location || 'TBA'}
                            </p>
                            <p class="text-xs text-orange-700 dark:text-orange-300">
                                ${event.extendedProps.isPast ? '✅ Completed' : '🔖 Available'}
                            </p>
                        </div>
                    `).join('')}
                </div>
            `,
            showConfirmButton: true,
            confirmButtonText: 'Close',
            width: '600px',
            customClass: {
                popup: 'events-preview-modal'
            }
        });
    };

    return (
        <>
            <div className="w-full p-3 md:p-5">
                
                <AddEventModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSubmit={handleAddEvent} />
                
                {modalView === 'detail' && (
                    <EventDetailModal 
                        event={selectedEvent} 
                        onClose={() => setSelectedEvent(null)}
                    />
                )}
                {modalView === 'history' && (
                    <EventHistoryModal 
                        event={selectedEvent}
                        onClose={() => setSelectedEvent(null)}
                    />
                )}
                
                <div className="flex flex-col lg:flex-row gap-5">
                    <aside className="w-full lg:w-[420px] xl:w-[480px] flex-shrink-0">
                        <CalendarSidebar 
                            user={user} 
                            stats={stats} 
                            onAddEventClick={() => setIsAddModalOpen(true)}
                        />
                    </aside>
                    <main className="flex-1 min-w-0">
                        <div className="h-[42vh] lg:h-[90vh] w-full glass-card p-4 sm:p-6">
                            <FullCalendar
                                key={String(isMobile)}
                                ref={calendarRef}
                                plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
                                headerToolbar={isMobile ? 
                                    { left: "title", right: "prev,next" } :
                                    { left: "prev,next today", center: "title", right: "dayGridMonth,dayGridWeek,listWeek" }
                                }
                                initialView={isMobile ? "listWeek" : "dayGridMonth"}
                                initialDate={searchParams.get('date') || new Date().toISOString().split('T')[0]}
                                events={calendarEvents}
                                eventClick={handleEventClick}
                                eventClassNames={getEventClassNames}
                                height="100%"
                                dayMaxEvents={2}  // Show max 2 events per day, then show "more"
                                eventContent={renderEventContent}
                                eventDisplay="block"
                                moreLinkClick="popover"
                                moreLinkText={(num) => `+${num} more`}
                                moreLinkClassNames="enhanced-more-link"
                            />
                        </div>
                    </main>
                </div>
            </div>
        </>
    );
};

export default Calendar;
